import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownLoader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {DropdownVirtualList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SearchDropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHeader';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {
  ContactModifiers,
  contactModifierTitleDescriptor,
} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {renderHighlightedPart} from '../../searchBar/suggestions/inboxesSearchSuggestionsHelpers';
import {ContactNameHandle} from '../searchModifiersHooks';
import {useSearchHintsContactSearch} from './useSearchHintsContactSearch';

const defaultSearchDropdownWidth = 400;
const minimalQueryLength = 2;

/*
 * Props.
 */

interface SearchHintContactDropdownProps extends PopoverTransferProps {
  modifier: ContactModifiers;
  selectedContacts: ReadonlyArray<ContactNameHandle>;

  cellsContext?: CellsContext;
  initialSuggestions?: ReadonlyArray<ContactNameHandle>;
  shouldHideHeaderTooltip?: boolean;

  onClick: (arg: string) => void;
  onClear: () => void;
}

type UiState = 'loading' | 'empty' | 'suggestions' | 'idle';

/*
 * Component.
 */

const StyledContactDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  line-height: 20px;
  font-size: ${fontSizes.medium};
  ${ellipsis()};
`;

const StyledNameSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledTooltip = styled.div`
  display: flex;
  line-height: 14px;
  div {
    margin: 0px 4px;
  }
`;

export const SearchHintContactDropdown = (props: SearchHintContactDropdownProps) => {
  const {
    selectedContacts,
    cellsContext,
    onClick,
    onClear,
    initialSuggestions,
    modifier,
    shouldHideHeaderTooltip,
    ...popoverProps
  } = props;
  const [query, setQuery] = useState<string>('');
  const {suggestions, status} = useSearchHintsContactSearch({
    query,
    cellsContext,
    initialSuggestions,
    selectedHandles: selectedContacts,
  });
  const localize = useLocalize();

  const localizedTitle = localize(contactModifierTitleDescriptor(modifier));

  return (
    <Dropdown {...popoverProps} width={defaultSearchDropdownWidth}>
      <SearchDropdownHeader
        title={
          <StyledTooltip>
            {localizedTitle}
            {!shouldHideHeaderTooltip && (
              <TooltipCoordinator
                maxWidth={256}
                render={() => (
                  <Trans id="search-hint-contact-dropdown-tooltip">
                    Conversations with any of the selected email addresses, along with other filters, will
                    appear in the search results.
                  </Trans>
                )}
              />
            )}
          </StyledTooltip>
        }
        rightIcon={
          selectedContacts.length > 0 ? (
            <Button
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
              onClick={() => {
                onClear();
                popoverProps.onRequestClose();
              }}
            >
              <Trans id="clear-refinement-button-link">Clear</Trans>
            </Button>
          ) : null
        }
        onSearchInputEnter={() => onClick(query)}
        placeholder="Search for name or email"
        shouldIncludeSearchIcon
        value={query}
        onChange={(newQuery: string) => {
          setQuery(newQuery);
        }}
      />
      {selectedContacts.length > 0 && query.length < minimalQueryLength ? (
        <ContactDropdown
          popoverProps={popoverProps}
          suggestions={selectedContacts}
          handleSelect={onClick}
          query={query}
          selectedHandles={selectedContacts}
        />
      ) : null}
      <Content
        suggestions={suggestions}
        addContact={onClick}
        popoverProps={popoverProps}
        status={status}
        query={query}
        selectedHandles={selectedContacts}
      />
    </Dropdown>
  );
};

/*
 * Sub-components.
 */

const SuggestionsComponent = ({
  suggestions,
  addContact,
  popoverProps,
  query,
  selectedHandles,
}: {
  suggestions: ReadonlyArray<ContactNameHandle>;
  addContact: (handle: string) => void;
  popoverProps: PopoverTransferProps;
  query: string;
  selectedHandles: ReadonlyArray<ContactNameHandle>;
}) => (
  <div style={{paddingTop: selectedHandles.length > 0 && query.length < minimalQueryLength ? '0px' : '8px'}}>
    {query.length < minimalQueryLength && (
      <DropdownCategory>
        <Trans id="search-hint-dropdown-suggestions">Suggestions</Trans>
      </DropdownCategory>
    )}
    <ContactDropdown
      popoverProps={popoverProps}
      suggestions={suggestions}
      handleSelect={addContact}
      query={query}
      selectedHandles={selectedHandles}
    />
  </div>
);

const Content = ({
  suggestions,
  addContact,
  popoverProps,
  status,
  query,
  selectedHandles,
}: {
  suggestions: ReadonlyArray<ContactNameHandle>;
  addContact: (handle: string) => void;
  popoverProps: PopoverTransferProps;
  status: AsyncStatusesEnum;
  query: string;
  selectedHandles: ReadonlyArray<ContactNameHandle>;
}) => {
  const uiState: UiState = useMemo(() => {
    if (query.length >= minimalQueryLength && status === AsyncStatusesEnum.LOADING) {
      return 'loading';
    }
    if (query.length < minimalQueryLength && suggestions.length === 0) {
      return 'idle';
    }
    if (suggestions.length === 0) {
      return 'empty';
    }
    return 'suggestions';
  }, [status, suggestions, query]);

  return {
    loading: <DropdownLoader status={AsyncStatusesEnum.LOADING} onRetryClick={() => ({})} />,
    empty: (
      <SuggestionsComponent
        suggestions={[
          {
            name: '',
            handle: query,
          },
        ]}
        addContact={addContact}
        popoverProps={popoverProps}
        query={query}
        selectedHandles={selectedHandles}
      />
    ),
    idle: null,
    suggestions: (
      <SuggestionsComponent
        suggestions={suggestions}
        addContact={addContact}
        popoverProps={popoverProps}
        query={query}
        selectedHandles={selectedHandles}
      />
    ),
  }[uiState];
};

/*
 * Helpers.
 */

const ContactDropdown = ({
  suggestions,
  popoverProps,
  handleSelect,
  query,
  selectedHandles,
}: {
  suggestions: ReadonlyArray<ContactNameHandle>;
  popoverProps: PopoverTransferProps;
  handleSelect: (handle: string) => void;
  query: string;
  selectedHandles: ReadonlyArray<ContactNameHandle>;
}) => {
  const isCheckboxChecked = useCallback(
    (contact: ContactNameHandle) => selectedHandles.some((e) => e.handle === contact.handle),
    [selectedHandles],
  );
  return (
    <DropdownVirtualList
      {...popoverProps}
      width={defaultSearchDropdownWidth}
      maxHeight={300}
      rowCount={suggestions.length}
      renderRow={(index) =>
        suggestions[index] ? (
          <DropdownItem
            onSelect={() => {
              handleSelect(suggestions[index].handle);
            }}
          >
            <DropdownItemContent>
              <StyledContactDiv>
                <StyledNameSpan>
                  {renderHighlightedPart(
                    suggestions[index].name || suggestions[index].handle,
                    undefined,
                    query,
                    true,
                    true,
                  )}
                </StyledNameSpan>{' '}
                {suggestions[index].name ? (
                  <>({renderHighlightedPart(suggestions[index].handle, undefined, query, true, true)})</>
                ) : null}
              </StyledContactDiv>
            </DropdownItemContent>
            <DropdownItemCheckbox isChecked={isCheckboxChecked(suggestions[index])} />
          </DropdownItem>
        ) : null
      }
    />
  );
};
